import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FancySelect from "./FancySelect";
import GoogleConnectBadge from "./GoogleConnectBadge";
import OutlookConnectBadge from "./OutlookConnectBadge";
import TeamDirectoryOrgChart from "./TeamDirectoryOrgChart";
import Avatar, { getPersonPhotoUrl } from "./Avatar";
import {
  AVATAR_TONES,
  DEFAULT_AVATAR_TONE,
  assignAvatarTones,
  getAvatarKey,
  normalizeAvatarColor,
} from "../lib/avatarPalette";
import googleLogo from "./../images/google-calendar.svg";
import outlookLogo from "./../images/outlook.svg";
import { Auth } from "../helpers/auth";
import {
  CALENDAR_PROVIDERS,
  CALENDAR_PROVIDER_LABEL,
  DISCONNECT_CONFIRM,
  disconnectOwnCalendar,
  fetchCalendarStatusByEmail,
} from "../lib/calendarConnections";
import { dialog } from "../lib/dialog";
import {
  describeDirectorySyncError,
  isDirectoryAddress,
  writableDirectoryEmails,
} from "../lib/workspaceDoc";
import {
  TEAM_SECURITY_LEVEL_OPTIONS,
  buildOrgChartAutoLayout,
  createTeamMember,
  getDefaultOrgChartPosition,
  getDirectReports,
  getTeamSecurityLabel,
  normalizeTeamPortraitStyle,
  normalizeTeamSecurityLevel,
  sanitizeTeamMembers,
  cleanGithubLogin,
} from "../lib/teamDirectory";

function getSupportedTimeZones() {
  if (typeof Intl?.supportedValuesOf === "function") {
    try {
      return Intl.supportedValuesOf("timeZone");
    } catch {}
  }
  return [
    "Etc/UTC",
    "America/Los_Angeles",
    "America/Denver",
    "America/Chicago",
    "America/New_York",
    "America/Phoenix",
    "America/Toronto",
    "America/Mexico_City",
    "America/Sao_Paulo",
    "Europe/London",
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Madrid",
    "Africa/Johannesburg",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];
}

function isValidTimeZone(tz) {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function getOffsetLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date());
    const off = parts.find((p) => p.type === "timeZoneName")?.value || "";
    return off.replace("GMT", "UTC");
  } catch {
    return "UTC+00:00";
  }
}

function getMemberLabel(member) {
  return String(member?.name || member?.email || "Unnamed teammate").trim();
}

function cleanEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function uniqueEmails(values = []) {
  const out = [];
  const seen = new Set();
  for (const value of values || []) {
    if (Array.isArray(value)) {
      for (const nested of uniqueEmails(value)) {
        if (!seen.has(nested)) {
          seen.add(nested);
          out.push(nested);
        }
      }
      continue;
    }

    const email = cleanEmail(value);
    if (!email || !email.includes("@") || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

// ---- Workspace access (TIER 2 invites) -------------------------------------
// The directory decides WHO may join this workspace; these helpers are only
// about telling those people, by email, that they can. The other invite path
// (components/CreateClientAccountCard.js) creates a brand new client company
// and is platform-admin only. Nothing here can touch another workspace: the
// server takes the workspace from the session, never from the request body.

function joinStateFor(entry, statusState) {
  if (statusState === "loading" || statusState === "idle") {
    return { label: "Checking...", tone: "" };
  }
  // A failed read means we do not know, which is not the same as "nobody has
  // been invited": saying the latter would send an admin chasing people who
  // already joined.
  if (statusState === "error") return { label: "Status unknown", tone: "" };
  if (entry?.hasPassword) return { label: "Joined", tone: "ok" };
  if (entry?.invitePending) return { label: "Invite sent", tone: "warn" };
  // An account with no password and no live token is an invite that timed out.
  // "Not invited yet" would be a lie here, and the admin would stop chasing
  // someone who is still locked out. The fix is the same either way: resend.
  if (entry?.hasAccount) return { label: "Invite expired", tone: "warn" };
  return { label: "Not invited yet", tone: "" };
}

// The button label lives next to joinStateFor on purpose: the two read the same
// states, so a row can never show a "Joined" chip beside a promise to resend an
// invite. Until the status read lands, and whenever it failed, we do not know
// which state a row is in, so the label says what the click actually does
// rather than guessing. The button stays live in that case: sending works
// whether or not the status read did.
function actionLabelFor(entry, statusState) {
  if (statusState !== "ready") return "Send link";
  // They finished signing up, so there is no password link to send. All the
  // server can do is tell them again that they have access.
  if (entry?.hasPassword) return "Email them again";
  if (entry?.hasAccount) return "Resend invite";
  return "Invite";
}

function inviteSuccessMessage(outcome, email) {
  const who = email || "They";
  switch (outcome) {
    case "resent":
      return `Fresh link sent to ${who}. Any older link has stopped working.`;
    case "already_has_account":
      return `${who} already had a BossDrop account, so no password link was needed. They were emailed to say they now have access to this workspace.`;
    default:
      return `Invite sent to ${who}. The link lets them set their own password, and it works for 7 days.`;
  }
}

function inviteErrorMessage(code, status, email) {
  const who = email || "That address";
  if (status === 429 || code === "rate_limited") {
    return "This workspace has sent a lot of invites in the last hour. Try again later.";
  }
  switch (code) {
    case "invalid_email":
      return `${who} isn't a valid email address. Fix it in the teammate's details, then invite them.`;
    case "forbidden_address":
      return "That address belongs to a BossDrop platform admin, so it can't be invited from here.";
    case "not_a_teammate":
      return `${who} isn't saved in this workspace's directory yet. Add them in the chart above, give the page a moment to save, then invite them.`;
    case "already_owner":
      return "That is the workspace owner, who already has access.";
    case "email_failed":
      return "The account is ready but the email didn't send. Use Resend invite in a minute.";
    case "forbidden":
      return "Only a workspace owner or admin can send invites.";
    case "unauthorized":
      return "Your session expired. Sign in again, then send the invite.";
    default:
      return "The invite didn't go out. Try again in a minute.";
  }
}

function getManagersForMember(member, teamMembers) {
  const byId = new Map(
    (teamMembers || []).map((teammate) => [teammate.id, teammate]),
  );
  return (member?.reportsTo || []).map((id) => byId.get(id)).filter(Boolean);
}

function getOrgSummary(member, teamMembers) {
  const managers = getManagersForMember(member, teamMembers);
  const directReports = getDirectReports(member?.id, teamMembers);
  const managerText = managers.length
    ? `Reports to ${managers.map((item) => getMemberLabel(item)).join(", ")}`
    : "Top-level org node";
  const reportText = directReports.length
    ? `Direct reports: ${directReports
        .map((item) => getMemberLabel(item))
        .join(", ")}`
    : "No direct reports yet";
  return `${managerText}. ${reportText}.`;
}

function getDefaultSelectedOrgMemberId(teamMembers) {
  const members = Array.isArray(teamMembers) ? teamMembers : [];
  if (!members.length) return null;
  const execMember = members.find((member) => member.execRecipient);
  if (execMember?.id) return execMember.id;

  const roots = members.filter(
    (member) => !Array.isArray(member?.reportsTo) || member.reportsTo.length === 0,
  );
  const pool = roots.length ? roots : members;
  return [...pool]
    .sort((left, right) => {
      const reportDelta =
        getDirectReports(right.id, members).length -
        getDirectReports(left.id, members).length;
      if (reportDelta !== 0) return reportDelta;
      return getMemberLabel(left).localeCompare(getMemberLabel(right));
    })[0]?.id || members[0]?.id || null;
}

function serializeTeamMemberContext(member, teamMembers) {
  return {
    name: member.name || "",
    email: member.email || "",
    role: member.role || "",
    tz: member.tz || "",
    securityLevel: getTeamSecurityLabel(member),
    reportsTo: getManagersForMember(member, teamMembers).map((person) => ({
      name: person.name || "",
      email: person.email || "",
      role: person.role || "",
    })),
    directReports: getDirectReports(member.id, teamMembers).map((person) => ({
      name: person.name || "",
      email: person.email || "",
      role: person.role || "",
    })),
  };
}

function wouldCreateCycle(teamMembers, managerId, reportId) {
  let frontier = [managerId];
  const visited = new Set();
  while (frontier.length) {
    const currentId = frontier.pop();
    if (!currentId || visited.has(currentId)) continue;
    if (currentId === reportId) return true;
    visited.add(currentId);
    const current = (teamMembers || []).find(
      (member) => member.id === currentId,
    );
    frontier = frontier.concat(
      Array.isArray(current?.reportsTo) ? current.reportsTo : [],
    );
  }
  return false;
}

function buildEditorState({ mode, member = null, localTZ, index = 0 }) {
  const base = member
    ? {
        ...member,
        reportsTo: [...(member.reportsTo || [])],
        orgChart: member.orgChart || getDefaultOrgChartPosition(index),
      }
    : createTeamMember(localTZ, index, index + 1);
  return {
    open: true,
    mode,
    memberId: member?.id || null,
    error: "",
    draft: base,
  };
}

function FancyTZSelect({
  value,
  onChange,
  options,
  placeholder = "Timezone (e.g., America/Chicago)",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const panelIdRef = useRef(
    `tz-panel-${Math.random().toString(36).slice(2, 10)}`,
  );

  const data = useMemo(
    () =>
      (options || []).map((tz) => {
        const off = getOffsetLabel(tz);
        const name = tz.replaceAll("_", " ");
        return {
          value: tz,
          label: `${name} (${off})`,
          search: `${tz.toLowerCase()} ${name.toLowerCase()} ${off.toLowerCase()}`,
        };
      }),
    [options],
  );

  const selected = useMemo(
    () => data.find((option) => option.value === value) || null,
    [data, value],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return data;
    const needle = q.trim().toLowerCase();
    return data.filter((option) => option.search.includes(needle));
  }, [data, q]);

  useEffect(() => {
    function onDocumentMouseDown(event) {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }
    setQ("");
    setActive(0);
  }, [open]);

  function commit(nextValue) {
    onChange?.(nextValue);
    setOpen(false);
    setQ("");
  }

  return (
    <div
      className={`select ${open ? "open" : ""}`}
      ref={wrapRef}
      style={{ position: "relative", width: "100%" }}
    >
      <button
        type="button"
        className="selectTrigger"
        data-placeholder={selected ? "false" : "true"}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelIdRef.current}
        style={{ width: "100%" }}
        title={selected ? selected.label : placeholder}
      >
        <span className="selectValue">
          {selected ? selected.label : placeholder}
        </span>
        <span className="selectCaret" aria-hidden />
      </button>

      <div
        id={panelIdRef.current}
        className="selectPanel"
        role="listbox"
        style={{ maxHeight: 320, overflow: "auto", left: 0, right: 0 }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            return;
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((index) => Math.min(index + 1, filtered.length - 1));
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            const pick = filtered[active];
            if (pick) commit(pick.value);
          }
        }}
      >
        <div style={{ padding: 6 }}>
          <input
            ref={inputRef}
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setActive(0);
            }}
            placeholder="Search time zones"
            aria-label="Search time zones"
            className="inputFancy"
            style={{ width: "100%", outline: "none" }}
          />
        </div>
        <div className="scrollable" style={{ padding: 6, paddingTop: 0 }}>
          {filtered.length === 0 ? (
            <div className="muted" style={{ padding: 10 }}>
              No matches
            </div>
          ) : (
            filtered.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = index === active;
              return (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  className={`selectOption ${isSelected ? "selected" : ""} ${isActive ? "active" : ""}`}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => commit(option.value)}
                >
                  {option.label}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function CheckGlyph() {
  return (
    <svg
      className="bdAvatarPicker__check"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3.5 8.4l2.9 2.9 6.1-6.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function listNames(names = []) {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
}

// What the color swatches in Edit teammate need to know, worked out against
// the directory with this draft in it: the color "Automatic" gives the draft
// right now, and for every color, which OTHER teammates would still have it if
// the draft picked it. Automatic teammates move off a picked color whenever a
// free one exists, so a name only shows up here when sharing is unavoidable:
// somebody else chose that color too, or every color is already in use.
function getAvatarChoice({ draft, members, mode, memberId }) {
  if (!draft) return null;
  const list = Array.isArray(members) ? members : [];
  const editing =
    mode === "edit" && list.some((member) => member.id === memberId);
  const withDraft = (avatarColor) => {
    const record = { ...draft, avatarColor };
    return editing
      ? list.map((member) => (member.id === memberId ? record : member))
      : [...list, record];
  };
  const draftKey = getAvatarKey(draft.name, draft.email);
  const others = list.filter((member) => {
    if (editing && member.id === memberId) return false;
    const key = getAvatarKey(member.name, member.email);
    return !!key && key !== draftKey;
  });
  const sharersOf = (assigned, tone) =>
    others
      .filter(
        (member) =>
          assigned.get(getAvatarKey(member.name, member.email)) === tone,
      )
      .map((member) => getMemberLabel(member));

  const autoAssigned = assignAvatarTones(withDraft(""));
  const autoTone =
    (draftKey && autoAssigned.get(draftKey)) || DEFAULT_AVATAR_TONE;
  const usedBy = {};
  AVATAR_TONES.forEach((tone) => {
    usedBy[tone.id] = sharersOf(assignAvatarTones(withDraft(tone.id)), tone);
  });
  return {
    autoTone,
    autoSharedWith: draftKey ? sharersOf(autoAssigned, autoTone) : [],
    // A new card with no name or email yet has no automatic color to show.
    autoPending: !draftKey,
    usedBy,
  };
}

// The badge preview, then "Automatic" and one swatch per palette color. The
// choice is saved on the teammate record as `avatarColor` (a palette key;
// automatic is no key at all), and every badge in the hub follows it through
// AvatarColorProvider.
function AvatarColorField({ draft, choice, disabled, onChange }) {
  const chosen = normalizeAvatarColor(draft?.avatarColor);
  const autoTone = choice?.autoTone || DEFAULT_AVATAR_TONE;
  const previewTone =
    (chosen && AVATAR_TONES.find((tone) => tone.id === chosen)) || autoTone;
  const sharedWith = chosen
    ? choice?.usedBy?.[chosen] || []
    : choice?.autoSharedWith || [];
  const name =
    String(draft?.name || "").trim() ||
    String(draft?.email || "").trim() ||
    "New teammate";

  let note;
  if (chosen) {
    note = sharedWith.length
      ? `${previewTone.label}, chosen for this teammate. ${listNames(sharedWith)} ${
          sharedWith.length === 1 ? "has" : "have"
        } it too.`
      : `${previewTone.label}, chosen for this teammate. No other teammate has it.`;
  } else if (choice?.autoPending) {
    note = "Automatic: a color no other teammate has, picked once there is a name or email.";
  } else {
    note = sharedWith.length
      ? `Automatic. Every color is in use, so ${previewTone.label} is shared with ${listNames(sharedWith)}.`
      : `Automatic: ${previewTone.label}, a color no other teammate has.`;
  }

  return (
    <div className="memberModal__field memberModal__field--wide">
      <span className="memberModal__label" id="memberModalAvatarColor">
        Avatar color
      </span>
      <div className="bdAvatarPicker">
        <div className="bdAvatarRow">
          <Avatar
            name={draft?.name}
            email={draft?.email}
            src={getPersonPhotoUrl(draft)}
            tone={previewTone.id}
            size={44}
          />
          <div className="bdAvatarRow__text">
            <strong>{name}</strong>
            <span className="muted small">{note}</span>
          </div>
        </div>
        <div
          className="bdAvatarPicker__options"
          role="group"
          aria-labelledby="memberModalAvatarColor"
        >
          <button
            type="button"
            className={`bdAvatarPicker__auto${chosen ? "" : " is-selected"}`}
            aria-pressed={!chosen}
            aria-label={
              choice?.autoPending
                ? "Automatic color"
                : `Automatic color, ${autoTone.label} right now`
            }
            title={
              choice?.autoPending
                ? "Automatic"
                : `Automatic: ${autoTone.label} right now`
            }
            disabled={disabled}
            onClick={() => onChange("")}
          >
            <span
              className="bdAvatarPicker__dot"
              style={{ "--bd-avatar-top": autoTone.top }}
              aria-hidden="true"
            />
            Automatic
          </button>
          {AVATAR_TONES.map((tone) => {
            const selected = chosen === tone.id;
            const users = choice?.usedBy?.[tone.id] || [];
            const label = users.length
              ? `${tone.label}, also used by ${listNames(users)}`
              : tone.label;
            return (
              <button
                key={tone.id}
                type="button"
                className={`bdAvatarPicker__swatch${
                  selected ? " is-selected" : ""
                }`}
                style={{ "--bd-avatar-top": tone.top }}
                aria-pressed={selected}
                aria-label={label}
                title={label}
                data-tone={tone.id}
                disabled={disabled}
                onClick={() => onChange(tone.id)}
              >
                {selected ? <CheckGlyph /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TeamMemberModal({
  editor,
  onClose,
  onSave,
  onDelete,
  onDraftChange,
  timeZones,
  conn,
  setConn,
  memberId,
  canSendNow,
  onSendNow,
  sendStatus,
  sending,
  memberSummary,
  readOnlyFields,
  canManageConnections,
  canDelete,
  // The card being opened is the VIEWER'S OWN. Only used to explain the
  // greying-out inside this dialog: the panel behind it says "owner only", but
  // an admin who opens their own card sees every field disabled with nothing in
  // the dialog itself to say why.
  isOwnCard = false,
  // { autoTone, autoSharedWith, autoPending, usedBy } from getAvatarChoice,
  // for the color swatches.
  avatarChoice = null,
  // This person's calendars from the workspace-wide list ({ google,
  // microsoft }), for a card the viewer cannot manage; null until it loads.
  calendarStatus = null,
  // Disconnect is offered on the viewer's own card only (isOwnCard).
  onDisconnectCalendar,
  disconnectingProvider = "",
  calendarRefreshKey = 0,
  disconnectResult = null,
  // Told when a badge here confirms a calendar is connected, so Workspace
  // access shows it without a reload.
  onCalendarConnected,
  apiBase,
}) {
  if (!editor.open) return null;

  const { draft, mode, error } = editor;
  const securityValue = normalizeTeamSecurityLevel(draft.securityLevel);
  // "Owner" is not a level anybody can HAND OUT. The owner is the account the
  // workspace belongs to, and the server stores a directory row that asks for
  // "owner" as "admin" -- so offering it here promised something the save
  // quietly changed. It stays in the list only while a row still carries it, so
  // an existing row is not silently relabelled (and then re-saved) as something
  // else; picking anything else replaces it for good.
  const securityOptions = TEAM_SECURITY_LEVEL_OPTIONS.filter(
    (option) => option.value !== "owner" || securityValue === "owner",
  ).map((option) =>
    option.value === "owner"
      ? { ...option, label: "Admin (was Owner)" }
      : option,
  );

  return (
    <div
      className="memberModalOverlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="memberModal"
        role="dialog"
        aria-modal="true"
        aria-label={mode === "create" ? "Add teammate" : "Edit teammate"}
      >
        <div className="memberModal__header">
          <div>
            <h4 className="memberModal__title">
              {mode === "create" ? "Add teammate" : "Edit teammate"}
            </h4>
            <p className="muted" style={{ marginTop: 4 }}>
              {readOnlyFields
                ? isOwnCard
                  ? "Only the workspace owner can change directory details. Your connections below are still yours to manage."
                  : "Only the workspace owner can change directory details. You can read this card and invite anyone already on it."
                : "Manage metadata here, then place and connect them in the chart."}
            </p>
          </div>
          <button type="button" className="closeBtn" onClick={onClose}>
            x
          </button>
        </div>

        <div className="memberModal__body">
          <div className="memberModal__grid">
            <label className="memberModal__field">
              <span className="memberModal__label">Name</span>
              <input
                className="inputFancy"
                value={draft.name}
                placeholder="Full name"
                disabled={readOnlyFields}
                onChange={(event) =>
                  onDraftChange({ name: event.target.value })
                }
              />
            </label>

            <label className="memberModal__field">
              <span className="memberModal__label">Email</span>
              <input
                className="inputFancy"
                type="email"
                value={draft.email}
                placeholder="name@company.com"
                disabled={readOnlyFields}
                onChange={(event) =>
                  onDraftChange({ email: event.target.value })
                }
              />
            </label>

            <label className="memberModal__field">
              <span className="memberModal__label">Role</span>
              <input
                className="inputFancy"
                value={draft.role}
                placeholder="Director of Operations"
                disabled={readOnlyFields}
                onChange={(event) =>
                  onDraftChange({ role: event.target.value })
                }
              />
            </label>
            <label className="memberModal__field">
              <span className="memberModal__label">GitHub username</span>
              <input
                className="inputFancy"
                value={draft.githubLogin || ""}
                placeholder="Optional, e.g. octocat"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                disabled={readOnlyFields}
                onChange={(event) =>
                  onDraftChange({ githubLogin: event.target.value })
                }
              />
            </label>
            <label className="memberModal__field memberModal__field--wide">
              <span className="memberModal__label">Timezone</span>
              <div
                style={{
                  pointerEvents: readOnlyFields ? "none" : undefined,
                  opacity: readOnlyFields ? 0.66 : 1,
                }}
              >
                <FancyTZSelect
                  value={draft.tz}
                  onChange={(tz) => onDraftChange({ tz })}
                  options={timeZones}
                />
              </div>
            </label>
            <AvatarColorField
              draft={draft}
              choice={avatarChoice}
              disabled={readOnlyFields}
              onChange={(avatarColor) => onDraftChange({ avatarColor })}
            />

            <label className="memberModal__field">
              <span className="memberModal__label">Security access</span>
              <FancySelect
                value={securityValue}
                disabled={readOnlyFields}
                onChange={(nextLevel) =>
                  onDraftChange({
                    securityLevel: nextLevel,
                    customSecurityLabel:
                      nextLevel === "custom" ? draft.customSecurityLabel : "",
                  })
                }
                options={securityOptions}
                placeholder="Choose access level"
                ariaLabel="Security access level"
                usePortal
              />
            </label>

            {securityValue === "custom" ? (
              <label className="memberModal__field memberModal__field--wide">
                <span className="memberModal__label">Custom access label</span>
                <input
                  className="inputFancy"
                  value={draft.customSecurityLabel || ""}
                  placeholder="Example: Finance approver"
                  disabled={readOnlyFields}
                  onChange={(event) =>
                    onDraftChange({
                      customSecurityLabel: event.target.value,
                    })
                  }
                />
              </label>
            ) : null}
          </div>

          <div className="memberModal__toggles">
            <label className="teamDirectoryToggle">
              <input
                type="checkbox"
                checked={!!draft.include}
                className="bd-check"
                disabled={readOnlyFields}
                onChange={(event) =>
                  onDraftChange({ include: event.target.checked })
                }
              />
              Include by default for scheduling
            </label>

            <label className="teamDirectoryToggle">
              <input
                type="checkbox"
                checked={!!draft.execRecipient}
                className="bd-check"
                disabled={readOnlyFields}
                onChange={(event) =>
                  onDraftChange({ execRecipient: event.target.checked })
                }
              />
              Send exec reports to this person
            </label>
          </div>

          <div className="memberModal__notes">
            <div className="memberModal__noteCard">
              <div className="memberModal__label">Org chart</div>
              <div className="muted">
                {memberSummary ||
                  "After saving, drag them into place and use Link direct report in the chart to define hierarchy."}
              </div>
            </div>

            <div className="memberModal__noteCard">
              <div className="memberModal__label">Calendar connections</div>
              {draft.email && memberId ? (
                <>
                  {/* A card the viewer cannot manage shows the state from
                      the workspace-wide list (read-only). Disconnect is
                      only ever on the viewer's own card. */}
                  <div className="memberModal__connections">
                    <GoogleConnectBadge
                      email={draft.email}
                      icon={googleLogo}
                      apiBase={apiBase}
                      disabled={!canManageConnections}
                      readOnly={!canManageConnections}
                      knownConnected={calendarStatus ? !!calendarStatus.google : undefined}
                      refreshKey={calendarRefreshKey}
                      onChange={(ok) => {
                        if (conn.google) setConn(memberId, "google", ok);
                        else if (ok) setConn(memberId, "google", true);
                        onCalendarConnected?.(draft.email, "google", ok);
                      }}
                      onDisconnect={
                        isOwnCard && typeof onDisconnectCalendar === "function"
                          ? () => onDisconnectCalendar("google")
                          : undefined
                      }
                      disconnecting={disconnectingProvider === "google"}
                    />
                    <OutlookConnectBadge
                      email={draft.email}
                      icon={outlookLogo}
                      apiBase={apiBase}
                      disabled={!canManageConnections}
                      readOnly={!canManageConnections}
                      knownConnected={calendarStatus ? !!calendarStatus.microsoft : undefined}
                      refreshKey={calendarRefreshKey}
                      onChange={(ok) => {
                        if (conn.outlook) setConn(memberId, "outlook", ok);
                        else if (ok) setConn(memberId, "outlook", true);
                        onCalendarConnected?.(draft.email, "microsoft", ok);
                      }}
                      onDisconnect={
                        isOwnCard && typeof onDisconnectCalendar === "function"
                          ? () => onDisconnectCalendar("microsoft")
                          : undefined
                      }
                      disconnecting={disconnectingProvider === "microsoft"}
                    />
                  </div>
                  {isOwnCard && disconnectResult?.msg ? (
                    <div
                      className={`memberModal__inlineStatus fieldHelp${
                        disconnectResult.ok ? "" : " error"
                      }`}
                      role="status"
                      style={{ marginTop: 8 }}
                    >
                      {disconnectResult.msg}
                    </div>
                  ) : null}
                </>
              ) : !canManageConnections ? (
                <div className="muted">
                  You can only connect calendar accounts for your own teammate
                  profile.
                </div>
              ) : (
                <div className="muted">
                  Save a teammate with an email to connect calendars.
                </div>
              )}
            </div>
          </div>

          {error ? <div className="fieldHelp error">{error}</div> : null}
          {sendStatus?.msg ? (
            <div
              className="memberModal__inlineStatus"
              style={{
                color:
                  sendStatus.ok === false
                    ? "var(--danger, #ff9a9a)"
                    : sendStatus.ok === true
                      ? "var(--success, #8ee59b)"
                      : "inherit",
              }}
            >
              {sendStatus.msg}
            </div>
          ) : null}
        </div>

        <div className="memberModal__actions">
          <div className="memberModal__actionsLeft">
            {mode === "edit" && canDelete ? (
              <button
                type="button"
                className="btn btnDangerGhost"
                onClick={() => onDelete(memberId)}
              >
                Delete teammate
              </button>
            ) : null}
          </div>

          <div className="memberModal__actionsRight">
            {canSendNow ? (
              <button
                type="button"
                className="btn"
                disabled={sending}
                aria-busy={sending}
                onClick={() => onSendNow(memberId)}
              >
                {sending ? "Sending..." : "Send now"}
              </button>
            ) : null}
            <button type="button" className="btn" onClick={onClose}>
              {readOnlyFields ? "Close" : "Cancel"}
            </button>
            {!readOnlyFields ? (
              <button type="button" className="btn primary" onClick={onSave}>
                {mode === "create" ? "Add teammate" : "Save changes"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TeamDirectorySettings({
  teamMembers,
  setTeamMembers,
  apiBase,
  currentUserEmail,
  workspaceOwnerEmail = "",
  // Defaults FALSE, like every other permission prop here. It gates the
  // "Workspace access" panel (join status and invite buttons), so a caller that
  // forgets to pass it must get the read-only directory, never the managed one.
  // The one call site (ControlRoom) passes it as `canManageWorkspaceAccess`.
  canManageWorkspace = false,
  // OWNER ONLY, and separate from canManageWorkspace on purpose: this list IS
  // the workspace's access map (the hub mirrors it into workspace_memberships),
  // so editing it hands out access rather than managing settings. Defaults
  // FALSE -- a caller that forgets to pass it gets a read-only directory, never
  // an editable one.
  canEditDirectory = false,
  // Admin-reachable: inviting somebody the owner already listed decides nobody's
  // access, it only tells them they have it.
  canInviteTeammates = false,
  canEditOwnConnections = true,
  // `{ error, email, status, scope }`, or null. scope "access": the last
  // POST /auth/workspace-memberships/sync was refused, so the DOCUMENT saved
  // and the ACCESS MAP did not. scope "doc": PUT /api/workspace/doc refused an
  // address the directory added, so NOTHING saved. This is the only place that
  // says either.
  directorySyncError = null,
  onDismissDirectorySyncError = null,
  // Owner-only actions, null for everyone else. Each resolves to
  // "ok" | "skipped" | "failed" and reports its own refusal.
  onRetryDirectorySync = null,
  onRemoveStrandedAccess = null,
  // Bumped by the caller after every mirror the access map accepted, so the
  // join-status read below is redone instead of compared against stale data.
  directoryMirrorVersion = 0,
  // A save (and so possibly a mirror) is queued or in flight. The "has access
  // but not in the directory" list waits for it rather than flash a teammate
  // the owner removed a moment ago.
  directorySavePending = false,
}) {
  const members = useMemo(
    () => sanitizeTeamMembers(teamMembers),
    [teamMembers],
  );
  const execId = useMemo(
    () => members.find((member) => member.execRecipient)?.id || null,
    [members],
  );
  const localTZ = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC",
    [],
  );
  const timeZones = useMemo(() => getSupportedTimeZones(), []);
  const [orgStatus, setOrgStatus] = useState("");
  const [selectedOrgMemberId, setSelectedOrgMemberId] = useState(null);
  const [editor, setEditor] = useState(() => ({
    open: false,
    mode: "create",
    memberId: null,
    error: "",
    draft: createTeamMember(localTZ, 0, 1),
  }));
  const [sendingId, setSendingId] = useState(null);
  const [statusById, setStatusById] = useState({});
  const [connById, setConnById] = useState({});
  const [reconnectByEmail, setReconnectByEmail] = useState({});
  // Workspace access: who on this directory actually has an account, and the
  // per-row invite that tells them they can sign in.
  const [joinStatusByEmail, setJoinStatusByEmail] = useState({});
  const [joinStatusState, setJoinStatusState] = useState("idle");
  // Separate from joinStatusState so the Refresh button can show its own busy
  // state without the whole column having to blank itself to say so.
  const [joinStatusRefreshing, setJoinStatusRefreshing] = useState(false);
  const [invitingEmails, setInvitingEmails] = useState([]);
  const [inviteResultByEmail, setInviteResultByEmail] = useState({});
  const hasInitializedSelectionRef = useRef(false);
  const normalizedCurrentUserEmail = useMemo(
    () => String(currentUserEmail || "").trim().toLowerCase(),
    [currentUserEmail],
  );
  const selfMember = useMemo(
    () =>
      members.find(
        (member) =>
          String(member?.email || "").trim().toLowerCase() ===
          normalizedCurrentUserEmail,
      ) || null,
    [members, normalizedCurrentUserEmail],
  );
  const selfMemberId = selfMember?.id || null;

  // Who has a calendar connected, for everyone in the workspace (any member
  // may read it): { [email]: { google, microsoft } }, or null until it loads.
  // The per-person status routes refuse a viewer who cannot manage that
  // person, so this list is what other people's cards and rows show.
  const [calendarByEmail, setCalendarByEmail] = useState(null);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [disconnectingProvider, setDisconnectingProvider] = useState("");
  const [disconnectResult, setDisconnectResult] = useState(null); // { ok, msg }
  const loadCalendarStatus = useCallback(async () => {
    const byEmail = await fetchCalendarStatusByEmail({ apiBase });
    if (byEmail) setCalendarByEmail(byEmail);
  }, [apiBase]);
  useEffect(() => {
    loadCalendarStatus();
  }, [loadCalendarStatus]);

  // A card's badge reports every status check: when the card opens, and every
  // 1.2 s while its connect popup is open. Workspace access reads this list,
  // so a connection made in the card shows there at once instead of after a
  // reload. Only a confirmed connection is copied: a failed check also reads
  // as "not connected", and Disconnect updates the list itself.
  const noteCalendarConnected = useCallback((email, provider, ok) => {
    const key = cleanEmail(email);
    if (!ok || !key || !CALENDAR_PROVIDERS.includes(provider)) return;
    setCalendarByEmail((current) =>
      !current || current[key]?.[provider] === true
        ? current
        : { ...current, [key]: { ...(current[key] || {}), [provider]: true } },
    );
  }, []);

  // Disconnect the viewer's OWN Google or Outlook connection. The server only
  // ever removes the signed-in person's, so nothing here names an address.
  const disconnectCalendar = useCallback(
    async (provider) => {
      const label = CALENDAR_PROVIDER_LABEL[provider];
      if (!label || !normalizedCurrentUserEmail || disconnectingProvider) return;
      const confirmed = await dialog.confirm({
        ...DISCONNECT_CONFIRM[provider],
        confirmLabel: "Disconnect",
        danger: true,
      });
      if (!confirmed) return;
      setDisconnectingProvider(provider);
      setDisconnectResult(null);
      try {
        await disconnectOwnCalendar({ apiBase, provider });
        setCalendarByEmail((current) => ({
          ...(current || {}),
          [normalizedCurrentUserEmail]: {
            ...((current || {})[normalizedCurrentUserEmail] || {}),
            [provider]: false,
          },
        }));
        if (selfMemberId) {
          setConnById((current) => ({
            ...current,
            [selfMemberId]: {
              ...(current[selfMemberId] || {}),
              [provider === "microsoft" ? "outlook" : "google"]: false,
            },
          }));
        }
        setCalendarRefreshKey((key) => key + 1);
        setDisconnectResult({ ok: true, msg: `${label} disconnected.` });
        loadCalendarStatus();
      } catch {
        setDisconnectResult({ ok: false, msg: `Couldn't disconnect ${label}. Try again.` });
      } finally {
        setDisconnectingProvider("");
      }
    },
    [apiBase, disconnectingProvider, loadCalendarStatus, normalizedCurrentUserEmail, selfMemberId],
  );
  // Whose card may be OPENED. An admin is not a directory editor any more, but
  // they may still open their own card, because the connector buttons inside it
  // are theirs (the fields themselves render read-only -- see readOnlyFields).
  const editableMemberIds = useMemo(
    () =>
      canEditDirectory
        ? members.map((member) => member.id)
        : selfMemberId
          ? [selfMemberId]
          : [],
    [canEditDirectory, members, selfMemberId],
  );
  const canEditMember = useCallback(
    (memberId) =>
      editableMemberIds.includes(String(memberId || "")),
    [editableMemberIds],
  );

  useEffect(() => {
    if (!members.length) {
      hasInitializedSelectionRef.current = false;
      setSelectedOrgMemberId(null);
      return;
    }
    if (!hasInitializedSelectionRef.current && selectedOrgMemberId == null) {
      hasInitializedSelectionRef.current = true;
      setSelectedOrgMemberId(getDefaultSelectedOrgMemberId(members));
      return;
    }
    if (selectedOrgMemberId != null) {
      hasInitializedSelectionRef.current = true;
    }
    if (
      selectedOrgMemberId != null &&
      !members.some((member) => member.id === selectedOrgMemberId)
    ) {
      setSelectedOrgMemberId(getDefaultSelectedOrgMemberId(members));
    }
  }, [members, selectedOrgMemberId]);

  useEffect(() => {
    if (!orgStatus) return undefined;
    const timer = window.setTimeout(() => setOrgStatus(""), 3200);
    return () => window.clearTimeout(timer);
  }, [orgStatus]);

  useEffect(() => {
    async function fetchConnStatus(member) {
      if (!member?.email) return;
      try {
        const base = (apiBase || "").replace(/\/+$/, "");
        const res = await fetch(
          `${base}/api/calendar/connections?email=${encodeURIComponent(
            member.email,
          )}`,
          { credentials: "include" },
        );
        if (!res.ok) throw new Error("status_fetch_failed");
        const json = await res.json();
        setConnById((current) => ({
          ...current,
          [member.id]: { google: !!json.google, outlook: !!json.outlook },
        }));
      } catch {}
    }

    members.forEach((member) => {
      const memberEmail = String(member?.email || "").trim().toLowerCase();
      const canInspectConnections =
        canManageWorkspace ||
        (normalizedCurrentUserEmail &&
          memberEmail &&
          memberEmail === normalizedCurrentUserEmail);
      if (member.email && !(member.id in connById) && canInspectConnections) {
        fetchConnStatus(member);
      }
    });
  }, [apiBase, canManageWorkspace, connById, members, normalizedCurrentUserEmail]);

  useEffect(() => {
    let cancelled = false;

    async function fetchConnectionHealth() {
      const base = (apiBase || "").replace(/\/+$/, "");
      const res = await fetch(`${base}/api/connections/health`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("connection_health_failed");
      const json = await res.json();
      if (cancelled) return;
      const nextMap = {};
      (Array.isArray(json?.connections) ? json.connections : []).forEach(
        (connection) => {
          const account = String(connection?.account || "").trim().toLowerCase();
          if (!account || !connection?.reconnectPath) return;
          if (!nextMap[account]) nextMap[account] = [];
          nextMap[account].push({
            provider: connection.provider,
            label: connection.label,
            reconnectPath: connection.reconnectPath,
            account: connection.account,
          });
        },
      );
      setReconnectByEmail(nextMap);
    }

    fetchConnectionHealth().catch(() => {});
    const timer = window.setInterval(() => {
      fetchConnectionHealth().catch(() => {});
    }, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [apiBase]);

  const normalizedOwnerEmail = useMemo(
    () => cleanEmail(workspaceOwnerEmail),
    [workspaceOwnerEmail],
  );

  // Which accepted mirror a join-status read reflects: a read that started
  // before the latest mirror cannot say who is still stranded after it.
  const mirrorVersionRef = useRef(directoryMirrorVersion);
  mirrorVersionRef.current = directoryMirrorVersion;
  const [joinStatusVersion, setJoinStatusVersion] = useState(-1);
  const appliedJoinVersionRef = useRef(-1);

  // One call for the whole directory, not one per row.
  const loadJoinStatus = useCallback(async () => {
    const version = mirrorVersionRef.current;
    // Only the first read, which has nothing to show yet, blanks the column.
    // A refetch after a send keeps the chips that are already on screen:
    // repainting every row as "Checking..." while one row's result is being
    // read throws away the information the admin came for.
    setJoinStatusState((current) =>
      current === "ready" ? current : "loading",
    );
    setJoinStatusRefreshing(true);
    try {
      const data = await Auth.workspaceJoinStatus();
      const next = {};
      (Array.isArray(data?.members) ? data.members : []).forEach((row) => {
        const key = cleanEmail(row?.email);
        if (!key) return;
        next[key] = {
          hasAccount: !!row?.hasAccount,
          hasPassword: !!row?.hasPassword,
          invitePending: !!row?.invitePending,
        };
      });
      // A read that started before a newer one already landed is stale.
      if (version < appliedJoinVersionRef.current) return;
      appliedJoinVersionRef.current = version;
      setJoinStatusByEmail(next);
      setJoinStatusVersion(version);
      setJoinStatusState("ready");
    } catch {
      // A failed status read must not hide the invite buttons: sending is what
      // matters, the chips are only a convenience.
      setJoinStatusState("error");
    } finally {
      setJoinStatusRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageWorkspace) {
      setJoinStatusByEmail({});
      setJoinStatusState("idle");
      setInviteResultByEmail({});
      return;
    }
    loadJoinStatus();
  }, [canManageWorkspace, loadJoinStatus]);

  // Re-read who has access after every mirror the access map accepted.
  const seenMirrorVersionRef = useRef(directoryMirrorVersion);
  useEffect(() => {
    if (seenMirrorVersionRef.current === directoryMirrorVersion) return;
    seenMirrorVersionRef.current = directoryMirrorVersion;
    if (canManageWorkspace) loadJoinStatus();
  }, [canManageWorkspace, directoryMirrorVersion, loadJoinStatus]);

  // The addresses the server turns into access-map rows for this directory
  // (same rule as the server: shaped, not the owner, de-duplicated).
  const writableEmails = useMemo(
    () => writableDirectoryEmails(members, normalizedOwnerEmail),
    [members, normalizedOwnerEmail],
  );

  // HAS ACCESS BUT IS NOT IN THE DIRECTORY. join-status reads the access map
  // itself (workspace_memberships, minus the owner's own record), and the only
  // writer of teammate rows there is the directory mirror, so an address it
  // lists that the directory does not is somebody whose removal never reached
  // the access map: the tab closed before the mirror ran, a "remove everyone"
  // was refused, the page reloaded before a retry. Nothing else can show it,
  // because the rows above are built from the directory. Hidden while a save
  // is queued or in flight and until a read newer than the last mirror lands,
  // so a teammate removed a moment ago does not flash here.
  const strandedEmails = useMemo(() => {
    if (!canManageWorkspace || joinStatusState !== "ready") return [];
    if (directorySavePending || joinStatusVersion !== directoryMirrorVersion) {
      return [];
    }
    const listed = new Set(writableEmails);
    return Object.keys(joinStatusByEmail)
      .filter(
        (address) =>
          !!address && address !== normalizedOwnerEmail && !listed.has(address),
      )
      .sort();
  }, [
    canManageWorkspace,
    directoryMirrorVersion,
    directorySavePending,
    joinStatusByEmail,
    joinStatusState,
    joinStatusVersion,
    normalizedOwnerEmail,
    writableEmails,
  ]);

  const [removingStranded, setRemovingStranded] = useState(false);
  // Owner only, and only on this click: never automatic. An ordinary sync of
  // the directory as it stands drops every unlisted row; with nobody left in
  // the directory the caller sends the explicit clear instead.
  async function removeStrandedAccess() {
    if (!canEditDirectory || typeof onRemoveStrandedAccess !== "function") return;
    if (!strandedEmails.length || removingStranded) return;
    const confirmed = await dialog.confirm({
      title: `Remove workspace access for ${strandedEmails.join(", ")}?`,
      body:
        "They are not in the Team directory, but they can still sign in to this workspace. " +
        "After this they cannot open it again until you add them to the directory. " +
        "Everyone in the directory keeps their access, and so do you.",
      confirmLabel: "Remove access",
      danger: true,
    });
    if (!confirmed) return;
    setRemovingStranded(true);
    try {
      await onRemoveStrandedAccess();
    } finally {
      setRemovingStranded(false);
      loadJoinStatus();
    }
  }

  const [retryingSync, setRetryingSync] = useState(false);
  async function retryDirectorySync() {
    if (typeof onRetryDirectorySync !== "function" || retryingSync) return;
    setRetryingSync(true);
    try {
      await onRetryDirectorySync();
    } finally {
      setRetryingSync(false);
    }
  }

  const accessRows = useMemo(() => {
    if (!canManageWorkspace) return [];
    const seen = new Set();
    return members
      .map((member) => ({ member, email: cleanEmail(member?.email) }))
      .filter(({ email }) => {
        if (!email || !email.includes("@") || seen.has(email)) return false;
        seen.add(email);
        return true;
      });
  }, [canManageWorkspace, members]);

  // Busy is per row, so sending to one teammate never freezes the rest of the
  // list, and a second click on the same row cannot fire a second send.
  async function sendWorkspaceInvite(email) {
    const target = cleanEmail(email);
    if (!target || invitingEmails.includes(target)) return;
    setInvitingEmails((current) => [...current, target]);
    setInviteResultByEmail((current) => ({ ...current, [target]: null }));
    try {
      const result = await Auth.inviteTeammate({ email: target });
      setInviteResultByEmail((current) => ({
        ...current,
        [target]: {
          ok: true,
          msg: inviteSuccessMessage(result?.outcome, target),
        },
      }));
      loadJoinStatus();
    } catch (error) {
      setInviteResultByEmail((current) => ({
        ...current,
        [target]: {
          ok: false,
          msg: inviteErrorMessage(error?.data?.error, error?.status, target),
        },
      }));
    } finally {
      setInvitingEmails((current) => current.filter((item) => item !== target));
    }
  }

  const countIncluded = useMemo(
    () => members.filter((member) => member.include).length,
    [members],
  );

  const totalReportingLines = useMemo(
    () =>
      members.reduce(
        (sum, member) =>
          sum + (Array.isArray(member.reportsTo) ? member.reportsTo.length : 0),
        0,
      ),
    [members],
  );

  const editorMember = useMemo(
    () => members.find((member) => member.id === editor.memberId) || null,
    [editor.memberId, members],
  );

  const avatarChoice = useMemo(
    () =>
      editor.open
        ? getAvatarChoice({
            draft: editor.draft,
            members,
            mode: editor.mode,
            memberId: editor.memberId,
          })
        : null,
    [editor.open, editor.draft, editor.mode, editor.memberId, members],
  );

  function updateMembers(updater) {
    setTeamMembers((prev) => sanitizeTeamMembers(updater(prev)));
  }

  function openCreateEditor() {
    if (!canEditDirectory) return;
    setEditor(
      buildEditorState({ mode: "create", localTZ, index: members.length }),
    );
  }

  function openEditEditor(memberId) {
    if (!canEditMember(memberId)) return;
    const member = members.find((item) => item.id === memberId);
    if (!member) return;
    setEditor(
      buildEditorState({
        mode: "edit",
        member,
        localTZ,
        index: members.findIndex((item) => item.id === memberId),
      }),
    );
  }

  function closeEditor() {
    setEditor((current) => ({ ...current, open: false, error: "" }));
  }

  async function applyEditorSave() {
    if (!canEditDirectory) return;
    const draft =
      editor.draft ||
      createTeamMember(localTZ, members.length, members.length + 1);
    const email = String(draft.email || "").trim();
    const tz = String(draft.tz || "").trim() || localTZ;
    if (!email) {
      setEditor((current) => ({
        ...current,
        error: "Email is required so BossDrop can route messages and meetings.",
      }));
      return;
    }
    // The server never turns an address of the wrong shape into access, so a
    // card saved with one looks like a teammate on screen while giving nobody
    // access (and, as the last card, kept the previous person's access alive).
    if (!isDirectoryAddress(email)) {
      setEditor((current) => ({
        ...current,
        error: "That email address doesn't look right. Use the form name@company.com.",
      }));
      return;
    }
    if (!isValidTimeZone(tz)) {
      setEditor((current) => ({
        ...current,
        error: "Please choose a valid IANA time zone.",
      }));
      return;
    }

    const existingMember =
      editor.mode === "edit"
        ? members.find((member) => member.id === editor.memberId)
        : null;
    const nextEmail = cleanEmail(email);
    const previousEmail = cleanEmail(existingMember?.email);
    const nextEmailAliases = uniqueEmails([
      draft.emailAliases,
      existingMember?.emailAliases,
      previousEmail && previousEmail !== nextEmail ? previousEmail : "",
    ])
      .filter((candidate) => candidate !== nextEmail)
      .slice(0, 12);

    const githubLoginInput = String(draft.githubLogin || "")
      .trim()
      .replace(/^@/, "");
    if (githubLoginInput && !cleanGithubLogin(githubLoginInput)) {
      setEditor((current) => ({
        ...current,
        error:
          "That GitHub username isn't valid. Use letters, numbers and single hyphens.",
      }));
      return;
    }

    const prepared = {
      ...draft,
      email: nextEmail,
      emailAliases: nextEmailAliases,
      githubLogin: cleanGithubLogin(githubLoginInput),
      tz,
      portraitStyle: normalizeTeamPortraitStyle(draft.portraitStyle),
      // "" (automatic) is dropped by sanitizeTeamMembers, so going back to
      // Automatic removes a saved color rather than storing a blank.
      avatarColor: normalizeAvatarColor(draft.avatarColor),
      securityLevel: normalizeTeamSecurityLevel(draft.securityLevel),
      customSecurityLabel:
        normalizeTeamSecurityLevel(draft.securityLevel) === "custom"
          ? String(draft.customSecurityLabel || "").trim()
          : "",
    };

    const applyEdit = (prev) => {
      if (editor.mode === "create") {
        const base = prepared.execRecipient
          ? prev.map((member) => ({ ...member, execRecipient: false }))
          : prev;
        return [...base, prepared];
      }

      return prev.map((member) => {
        if (member.id === editor.memberId) return { ...member, ...prepared };
        if (prepared.execRecipient) return { ...member, execRecipient: false };
        return member;
      });
    };

    // An edit can empty the access map too: the last teammate's address
    // changed to the owner's own, which never counts as a teammate. The save
    // then sends the "remove everyone" clear, so ask first, exactly as the
    // card's delete does.
    if (editor.mode === "edit") {
      const writableBefore = writableDirectoryEmails(members, normalizedOwnerEmail);
      const writableAfter = writableDirectoryEmails(
        applyEdit(members),
        normalizedOwnerEmail,
      );
      if (writableBefore.length > 0 && writableAfter.length === 0) {
        const confirmed = await dialog.confirm({
          title: `Save ${getMemberLabel(prepared)} with this address?`,
          body:
            "After this change nobody in the Team directory has a teammate address " +
            "(the workspace owner's own address does not count), so saving also removes " +
            `workspace access for ${writableBefore.join(", ")}. Your own owner access is not affected.`,
          confirmLabel: "Save and remove access",
          danger: true,
        });
        if (!confirmed) return;
      }
    }

    updateMembers(applyEdit);

    setSelectedOrgMemberId(prepared.id);
    closeEditor();
    setOrgStatus(
      editor.mode === "create"
        ? `Added ${getMemberLabel(prepared)} to the org chart.`
        : `Updated ${getMemberLabel(prepared)}.`,
    );
  }

  function commitMemberPosition(id, orgChart) {
    if (!canEditDirectory) return;
    updateMembers((prev) => {
      let changed = false;
      const next = prev.map((member) => {
        if (member.id !== id) return member;
        const current = member.orgChart || {};
        if (
          Number(current.x) === Number(orgChart?.x) &&
          Number(current.y) === Number(orgChart?.y)
        ) {
          return member;
        }
        changed = true;
        return { ...member, orgChart };
      });
      return changed ? next : prev;
    });
  }

  function commitMemberPositions(positionMap) {
    if (!canEditDirectory) return;
    const updates = positionMap && typeof positionMap === "object"
      ? positionMap
      : {};
    updateMembers((prev) => {
      let changed = false;
      const next = prev.map((member) => {
        const orgChart = updates[member.id];
        if (!orgChart) return member;
        const current = member.orgChart || {};
        if (
          Number(current.x) === Number(orgChart?.x) &&
          Number(current.y) === Number(orgChart?.y)
        ) {
          return member;
        }
        changed = true;
        return { ...member, orgChart };
      });
      return changed ? next : prev;
    });
  }

  function autoLayoutMembers() {
    if (!canEditDirectory) return;
    updateMembers((prev) => {
      const nextLayout = buildOrgChartAutoLayout(prev);
      return prev.map((member, index) => ({
        ...member,
        orgChart:
          nextLayout[member.id] ||
          getDefaultOrgChartPosition(index, prev.length),
      }));
    });
    setOrgStatus("Auto-layout applied.");
  }

  async function deleteMember(id) {
    if (!canEditDirectory) return;
    const removed = members.find((member) => member.id === id);
    if (!removed) return;
    // REMOVING THE LAST TEAMMATE EMPTIES THE ACCESS MAP. "Last" is counted
    // the way the server counts rows (writableDirectoryEmails): the owner's own
    // card and rows with no usable address never become access, so removing
    // the last real teammate beside them leaves zero rows, which the server
    // only accepts as a deliberate "remove everyone" (`clearDirectory: true`).
    // ControlRoom sends exactly that when a save takes the writable rows from
    // some to none, so this is the moment to make sure it is what the owner
    // meant: the person being removed keeps their password and their session,
    // and losing their access is the whole point of the action.
    const writableBefore = writableDirectoryEmails(members, normalizedOwnerEmail);
    const writableAfter = writableDirectoryEmails(
      members.filter((member) => member.id !== id),
      normalizedOwnerEmail,
    );
    const isLastMember =
      writableBefore.includes(cleanEmail(removed?.email)) &&
      writableAfter.length === 0;
    if (isLastMember) {
      const label = getMemberLabel(removed);
      const confirmed = await dialog.confirm({
        title: `Remove ${label} from this workspace?`,
        body:
          "They are the last teammate in the Team directory, so this also removes their access: " +
          "they will not be able to open this workspace again until you add them back. " +
          "Your own owner access is not affected.",
        confirmLabel: "Remove",
        danger: true,
      });
      if (!confirmed) return;
    }
    updateMembers((prev) => prev.filter((member) => member.id !== id));
    if (editor.open && editor.memberId === id) closeEditor();
    // Not "removed their access": that happens when the save's mirror is
    // accepted, and a refusal shows on the banner above.
    setOrgStatus(
      isLastMember
        ? `Removed ${getMemberLabel(removed)}. Their access to this workspace ends as soon as this change saves.`
        : `Removed ${getMemberLabel(removed)} from the org chart.`,
    );
  }

  function toggleReportingLine(managerId, reportId) {
    if (!canEditDirectory) return;
    if (!managerId || !reportId || managerId === reportId) return;
    const report = members.find((member) => member.id === reportId);
    const alreadyLinked = !!report?.reportsTo?.includes(managerId);
    if (!alreadyLinked && wouldCreateCycle(members, managerId, reportId)) {
      setOrgStatus(
        "That reporting line would create a loop, so I left it unchanged.",
      );
      return;
    }

    updateMembers((prev) =>
      prev.map((member) => {
        if (member.id !== reportId) return member;
        const currentLinks = Array.isArray(member.reportsTo)
          ? member.reportsTo
          : [];
        return {
          ...member,
          reportsTo: alreadyLinked
            ? currentLinks.filter((id) => id !== managerId)
            : [...currentLinks, managerId],
        };
      }),
    );

    const manager = members.find((member) => member.id === managerId);
    const reportMember = members.find((member) => member.id === reportId);
    setOrgStatus(
      alreadyLinked
        ? `Removed the reporting line from ${getMemberLabel(manager)} to ${getMemberLabel(reportMember)}.`
        : `Linked ${getMemberLabel(reportMember)} under ${getMemberLabel(manager)}.`,
    );
  }

  async function sendNow(memberId) {
    if (!canManageWorkspace) return;
    const member = members.find((item) => item.id === memberId);
    if (!member?.email) {
      setStatusById((current) => ({
        ...current,
        [memberId]: { ok: false, msg: "Add an email first." },
      }));
      return;
    }

    try {
      setSendingId(member.id);
      setStatusById((current) => ({
        ...current,
        [member.id]: { ok: null, msg: "Generating summary and sending..." },
      }));

      const includedTeam = members
        .filter((teammate) => teammate.include)
        .map((teammate) => serializeTeamMemberContext(teammate, members));

      const base = (apiBase || "").replace(/\/+$/, "");
      const res = await fetch(`${base}/api/exec-reports/send-now`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipientEmail: member.email,
          recipientName: member.name || member.email,
          team: includedTeam,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed (${res.status})`);
      }

      setStatusById((current) => ({
        ...current,
        [member.id]: { ok: true, msg: "Report sent." },
      }));
    } catch (error) {
      setStatusById((current) => ({
        ...current,
        [memberId]: {
          ok: false,
          msg: `Failed: ${error?.message || "Unknown error"}`,
        },
      }));
    } finally {
      setSendingId(null);
    }
  }

  function setConn(id, provider, ok) {
    setConnById((current) => ({
      ...current,
      [id]: { ...(current[id] || {}), [provider]: !!ok },
    }));
  }

  const editorCanManageConnections = useMemo(() => {
    const draftEmail = String(editor?.draft?.email || "")
      .trim()
      .toLowerCase();
    if (canManageWorkspace) return true;
    return !!(
      canEditOwnConnections &&
      normalizedCurrentUserEmail &&
      draftEmail &&
      draftEmail === normalizedCurrentUserEmail
    );
  }, [
    canEditOwnConnections,
    canManageWorkspace,
    editor?.draft?.email,
    normalizedCurrentUserEmail,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crTitle">Team Directory</h3>
      <p className="muted" style={{ marginTop: 4 }}>
        Build the org chart visually, then edit teammate metadata in-place from
        the chart itself.
      </p>

      {/* One line, so nobody loses a control without being told why. Shown to
          an admin, who can still manage the rest of the workspace: the
          directory is the access map, so it belongs to the owner. */}
      {canManageWorkspace && !canEditDirectory ? (
        <div className="infoBanner" style={{ marginTop: 8 }}>
          <div className="muted small">
            <strong>Owner only.</strong> This list decides who can sign in to
            the workspace, so only the workspace owner can change it. You can
            read it, open your own card, and invite anyone already on it.
          </div>
        </div>
      ) : null}

      {/* THE DIRECTORY AND THE ACCESS MAP DISAGREE. The list below saved; the
          server refused to apply it to who can actually sign in, and named the
          address it refused. Every one of these used to be discarded by a
          `.catch(() => {})`, so the save looked like it worked.
          scope "doc" is the other refusal: the document save itself refused
          an address the directory added, so NOTHING saved and every later save
          fails the same way until that address is removed. */}
      {directorySyncError ? (
        <div
          className="infoBanner"
          role="alert"
          style={{ marginTop: 8, borderColor: "var(--danger, #c0392b)" }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {directorySyncError.scope === "doc" ? (
              <>Your changes weren&rsquo;t saved</>
            ) : (
              <>Workspace access wasn&rsquo;t updated</>
            )}
          </div>
          <div className="muted small">
            {describeDirectorySyncError(directorySyncError)}
          </div>
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}
          >
            {/* Re-sends the mirror. Not offered for a "doc" refusal, which
                only removing the named address can fix. */}
            {directorySyncError.scope !== "doc" &&
            canEditDirectory &&
            typeof onRetryDirectorySync === "function" ? (
              <button
                type="button"
                className="btn"
                onClick={retryDirectorySync}
                disabled={retryingSync}
                aria-busy={retryingSync}
              >
                {retryingSync ? "Trying..." : "Try again"}
              </button>
            ) : null}
            {onDismissDirectorySyncError ? (
              <button
                type="button"
                className="btn"
                onClick={onDismissDirectorySyncError}
              >
                Dismiss
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {orgStatus ? (
        <div className="orgChartStatus" role="status" aria-live="polite">
          {orgStatus}
        </div>
      ) : null}

      <TeamDirectoryOrgChart
        teamMembers={members}
        selectedId={selectedOrgMemberId}
        onSelect={setSelectedOrgMemberId}
        onAutoLayout={autoLayoutMembers}
        onCommitPosition={commitMemberPosition}
        onCommitPositions={commitMemberPositions}
        onToggleReportingLine={toggleReportingLine}
        onOpenCreate={openCreateEditor}
        onOpenEdit={openEditEditor}
        onDeleteMember={deleteMember}
        onSendNow={sendNow}
        sendingId={sendingId}
        statusById={statusById}
        reconnectByEmail={reconnectByEmail}
        apiBase={apiBase}
        getOrgSummary={getOrgSummary}
        canEditDirectory={canEditDirectory}
        // Owner OR admin: the exec report's "Send now" hands out no access,
        // and POST /api/exec-reports/send-now is requireWorkspaceAdmin.
        canSendReports={canManageWorkspace}
        editableMemberIds={editableMemberIds}
      />

      <TeamMemberModal
        editor={editor}
        onClose={closeEditor}
        onSave={applyEditorSave}
        onDelete={deleteMember}
        onDraftChange={(patch) =>
          setEditor((current) => ({
            ...current,
            error: "",
            draft: { ...current.draft, ...patch },
          }))
        }
        timeZones={timeZones}
        conn={connById[editor.memberId] || {}}
        setConn={setConn}
        memberId={editor.memberId}
        canSendNow={
          // sendNow() returns early without canManageWorkspace, so a plain
          // member must not see a button that silently does nothing.
          !!(
            canManageWorkspace &&
            editor.draft?.execRecipient &&
            editor.memberId &&
            editor.draft?.email
          )
        }
        onSendNow={sendNow}
        sendStatus={statusById[editor.memberId]}
        sending={sendingId === editor.memberId}
        memberSummary={
          editor.mode === "edit" && editorMember
            ? getOrgSummary(editorMember, members)
            : ""
        }
        readOnlyFields={!canEditDirectory}
        isOwnCard={
          !!editorMember &&
          !!currentUserEmail &&
          cleanEmail(editorMember.email) === cleanEmail(currentUserEmail)
        }
        canManageConnections={editorCanManageConnections}
        canDelete={!!(canEditDirectory && editor.mode === "edit")}
        avatarChoice={avatarChoice}
        calendarStatus={
          calendarByEmail ? calendarByEmail[cleanEmail(editor?.draft?.email)] || null : null
        }
        onDisconnectCalendar={disconnectCalendar}
        disconnectingProvider={disconnectingProvider}
        calendarRefreshKey={calendarRefreshKey}
        disconnectResult={disconnectResult}
        onCalendarConnected={noteCalendarConnected}
        apiBase={apiBase}
      />

      {/* Workspace access: admins only. A member with no rights over the
          workspace sees the directory exactly as before, with no status and
          no buttons. */}
      {canManageWorkspace ? (
        <div className="teamAccess">
          <div className="teamAccess__head">
            <div className="teamAccess__intro">
              <div className="itemTitle">Workspace access</div>
              <p className="muted small" style={{ marginTop: 4 }}>
                Inviting someone here emails them a link into{" "}
                <strong>this</strong> workspace, where they choose their own
                password. BossDrop will only send a link to an address that is
                already saved in the directory above:{" "}
                {canEditDirectory
                  ? "add the teammate to the chart first, and the directory saves itself."
                  : "ask the workspace owner to add anyone who is missing from it."}
              </p>
            </div>
            <button
              type="button"
              className="btn"
              onClick={loadJoinStatus}
              disabled={joinStatusRefreshing}
              aria-busy={joinStatusRefreshing}
            >
              {joinStatusRefreshing ? "Checking..." : "Refresh"}
            </button>
          </div>

          {joinStatusState === "error" ? (
            <div className="teamAccess__notice" role="status">
              Couldn't check who has joined. You can still send invites.
            </div>
          ) : null}

          {disconnectResult?.msg ? (
            <div
              className={`teamAccess__notice${disconnectResult.ok ? "" : " bad"}`}
              role="status"
            >
              {disconnectResult.msg}
            </div>
          ) : null}

          {accessRows.length ? (
            <div className="teamAccess__list">
              {accessRows.map(({ member, email }) => {
                const isOwner =
                  !!normalizedOwnerEmail && email === normalizedOwnerEmail;
                const entry = joinStatusByEmail[email];
                const state = isOwner
                  ? { label: "Joined", tone: "ok" }
                  : joinStateFor(entry, joinStatusState);
                const busy = invitingEmails.includes(email);
                const result = inviteResultByEmail[email];
                const label = actionLabelFor(entry, joinStatusState);
                return (
                  <div className="teamAccess__row" key={member.id}>
                    <div className="teamAccess__who bdAvatarRow">
                      <Avatar
                        name={member.name}
                        email={email}
                        src={getPersonPhotoUrl(member)}
                        size={34}
                      />
                      <div className="bdAvatarRow__text">
                        <div className="itemTitle">{getMemberLabel(member)}</div>
                        <div className="muted small" title={email}>
                          {email}
                        </div>
                      </div>
                    </div>
                    {/* Everyone's calendars are visible here; Disconnect is
                        on the viewer's own row only. */}
                    <div className="teamAccess__calendars" aria-label={`Calendars for ${email}`}>
                      {CALENDAR_PROVIDERS.map((provider) => {
                        const on = calendarByEmail?.[email]?.[provider] === true;
                        const providerLabel = CALENDAR_PROVIDER_LABEL[provider];
                        const isSelf =
                          !!normalizedCurrentUserEmail && email === normalizedCurrentUserEmail;
                        return (
                          <div className="teamAccess__cal" key={provider}>
                            <img
                              src={provider === "google" ? googleLogo : outlookLogo}
                              alt=""
                              aria-hidden="true"
                            />
                            <span className={`teamAccess__calState${on ? " is-on" : ""}`}>
                              {providerLabel}:{" "}
                              {!calendarByEmail ? "Checking" : on ? "Connected" : "Not connected"}
                            </span>
                            {isSelf && on ? (
                              <button
                                type="button"
                                className="teamAccess__disconnect"
                                onClick={() => disconnectCalendar(provider)}
                                disabled={!!disconnectingProvider}
                                aria-busy={disconnectingProvider === provider}
                                aria-label={`Disconnect ${providerLabel}`}
                              >
                                {disconnectingProvider === provider ? "Disconnecting..." : "Disconnect"}
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    <div className="teamAccess__state">
                      <span
                        className={`chip ${state.tone}`.trim()}
                        title={
                          isOwner
                            ? "This workspace belongs to them."
                            : undefined
                        }
                      >
                        {state.label}
                      </span>
                    </div>
                    <div className="teamAccess__action">
                      {isOwner ? (
                        <span className="muted small">Workspace owner</span>
                      ) : (
                        <button
                          type="button"
                          className="btn"
                          disabled={busy || !canInviteTeammates}
                          aria-busy={busy}
                          title={
                            canInviteTeammates
                              ? undefined
                              : "Only the workspace owner or an admin can send invites."
                          }
                          onClick={() => sendWorkspaceInvite(email)}
                        >
                          {busy ? "Sending..." : label}
                        </button>
                      )}
                    </div>
                    {result?.msg ? (
                      <div
                        className={`teamAccess__result ${
                          result.ok ? "ok" : "bad"
                        }`}
                        role="status"
                      >
                        {result.msg}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="teamAccess__notice">
              Add a teammate with an email address in the chart above, then
              send them a link from here.
            </div>
          )}

          {/* Read from the access map, not the directory: the rows above are
              built from the directory, so a removal that never reached the
              access map was invisible everywhere. */}
          {strandedEmails.length ? (
            <div style={{ marginTop: 16 }} role="alert">
              <div className="itemTitle">Has access but not in the directory</div>
              <p className="muted small" style={{ marginTop: 4 }}>
                These addresses can still sign in to this workspace even though
                they are not in the Team directory above.{" "}
                {canEditDirectory
                  ? "Remove their access, or add them back to the directory if they should keep it."
                  : "Only the workspace owner can remove their access."}
              </p>
              <div className="teamAccess__list">
                {strandedEmails.map((address) => (
                  <div className="teamAccess__row" key={address}>
                    <div className="teamAccess__who bdAvatarRow">
                      <Avatar email={address} size={34} />
                      <div className="bdAvatarRow__text">
                        <div className="muted small" title={address}>
                          {address}
                        </div>
                      </div>
                    </div>
                    <div className="teamAccess__calendars" />
                    <div className="teamAccess__state">
                      <span className="chip bad">Has access</span>
                    </div>
                    <div className="teamAccess__action" />
                  </div>
                ))}
              </div>
              {canEditDirectory &&
              typeof onRemoveStrandedAccess === "function" ? (
                <button
                  type="button"
                  className="btn btnDangerGhost"
                  style={{ marginTop: 10 }}
                  onClick={removeStrandedAccess}
                  disabled={removingStranded}
                  aria-busy={removingStranded}
                >
                  {removingStranded ? "Removing..." : "Remove their access"}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="muted" style={{ marginTop: 12 }}>
        Included by default: <strong>{countIncluded}</strong>
        {" | Reporting lines: "}
        <strong>{totalReportingLines}</strong>
        {execId ? (
          <>
            {" | "}Exec recipient:{" "}
            <strong>
              {getMemberLabel(members.find((member) => member.id === execId))}
            </strong>
          </>
        ) : null}
      </div>
    </div>
  );
}
